import { SalesCommissionRatesSchema, STANDARD_SALES_DIRECT_POLICY, STANDARD_SALES_DIRECT_POLICY_FLAT } from '../types/sales-team.js';
import { salesAddMonths, salesMonth } from './sales-commission.js';
import { salesPlanAt, salesAssignmentAt, salesEmploymentAt } from './sales-team-policy.js';
import { salesWorkAt } from './sales-rep-program.js';
/** The team active-partner breakpoints shared by the team hardware override and, from standard revision 3, team session points (ruling 4). */
export const SALES_TEAM_BREAKPOINTS = [1, 50, 100];
/** The launch stamp maximum: a Golden 5.00 partner rate plus both 2.00 overrides, so EVI keeps 6.00 of its 15 points (ruling 7). */
export const SALES_STAMP_MAX_BPS = 900;
/** The newest standard defined in code. The plan form prefills it until a stored standard of this revision or later exists. */
export const SALES_CODE_STANDARD_REVISION = 3;
/**
 * Seed input only. Runtime forms and earnings read the stored standard or accepted plan. The Stage 2 base salary is never written
 * here: the reviewed standard script passes it from its flags, and it is added only to stage_2 when passed. Revision 2 values are frozen:
 * stored standard-2 is compared against them.
 */
export function salesStandardV2(stage = 'stage_1', base) {
    return salesStandardRates(stage, base, { own_session_points: STANDARD_SALES_DIRECT_POLICY,
        partner_session_points: [{ min_active_partners: 1, rate_bps: 50 }, { min_active_partners: 10, rate_bps: 75 }, { min_active_partners: 25, rate_bps: 100 }],
        team_session_points: 50 });
}
/**
 * Standard revision 3 (sales comp change, 27 October 2026, rulings 3 to 5), the same session values in both stages: own deals 300 flat,
 * SP Manager partner_session_points 100 / 150 / 200 at 1 / 10 / 25 active partners, Sales Manager team_session_points 100 / 150 / 200 at
 * 1 / 50 / 100 team active partners. Every other category is revision 2's. Seed and prefill input only; stamps read accepted plans.
 */
export function salesStandardV3(stage = 'stage_1', base) {
    return salesStandardRates(stage, base, { own_session_points: STANDARD_SALES_DIRECT_POLICY_FLAT,
        partner_session_points: [{ min_active_partners: 1, rate_bps: 100 }, { min_active_partners: 10, rate_bps: 150 }, { min_active_partners: 25, rate_bps: 200 }],
        team_session_points: SALES_TEAM_BREAKPOINTS.map((min_active_partners, i) => ({ min_active_partners, rate_bps: [100, 150, 200][i] })) });
}
/**
 * The Sales Staff plan form prefill: the newer of the stored standard and the code standard, by revision. A stored revision at or above
 * SALES_CODE_STANDARD_REVISION wins. Otherwise the code standard, whose Stage 2 set carries the stored Stage 2 base salary and is absent
 * when the stored standard has none (a base is never invented). Recording the code standard as a stored row stays a separate data step.
 */
export function salesStandardPrefill(stored) {
    if (stored.revision >= SALES_CODE_STANDARD_REVISION)
        return { ...stored, source: 'stored' };
    const base = stored.stage2_rates?.stage === 'stage_2' ? stored.stage2_rates.base_salary : undefined;
    return { revision: SALES_CODE_STANDARD_REVISION, source: 'code', rates: salesStandardV3('stage_1'), stage2_rates: base ? salesStandardV3('stage_2', base) : null };
}
function salesStandardRates(stage, base, session) {
    const employee = stage === 'stage_2';
    return SalesCommissionRatesSchema.parse({
        version: 2, stage, annual_threshold_currency: 'USD',
        direct_hardware_margin: { rate_bps: employee ? 1000 : 2500, accelerator_rate_bps: employee ? 1250 : 3000, annual_margin_threshold_cents: { USD: 15_000_000 } },
        // Every fleet renewal pays while on the team (Warren, 18 October 2026): months null means no month limit.
        subscription_share: { rate_bps: employee ? 1000 : 2000, months: null, scope: 'fleet_orders_only' },
        own_session_points: session.own_session_points,
        partner_session_points: session.partner_session_points,
        session_points_basis: 'evi_percentage_points',
        partner_activation: { currency: 'USD', first_order_cents: employee ? 25000 : 50000, fourth_charger_cents: employee ? 50000 : 100000 },
        partner_book_margin: { months: null, ladder: [{ min_active_partners: 1, rate_bps: employee ? 500 : 1000 }, { min_active_partners: 10, rate_bps: employee ? 1000 : 2000 }, { min_active_partners: 25, rate_bps: employee ? 1250 : 2500 }] },
        continuation_months: 12, golden_bonus: { currency: 'USD', amount_cents: employee ? 100000 : 250000 },
        team_override_margin: { ladder: SALES_TEAM_BREAKPOINTS.map((min_active_partners, i) => ({ min_active_partners, rate_bps: (employee ? [250, 375, 500] : [500, 750, 1000])[i] })) },
        team_growth_bonus: { currency: 'USD', six_active_cents: employee ? 125000 : 250000, payroll_trigger_cents: employee ? 250000 : 500000 },
        team_session_points: session.team_session_points,
        payroll_eligibility: { rep_active_partners: 12, manager_active_partners: 36, consecutive_months: 3, average_months: 6, usd_average_cents: 850000 },
        ...(employee && base ? { base_salary: { currency: 'USD', rep_annual_cents: base.rep_annual_cents, manager_annual_cents: base.manager_annual_cents } } : {}),
    });
}
export function salesManagerAt(person, at) {
    const history = person.manager_assignment_history ?? [];
    const row = [...history].filter(h => +new Date(h.effective_at) <= +at).sort((a, b) => +new Date(b.effective_at) - +new Date(a.effective_at))[0];
    return row ? row.manager_id ? String(row.manager_id) : null : history.length ? null : person.manager_id ? String(person.manager_id) : null;
}
export function salesRoleAt(person, at, fallback = 'rep') {
    const row = [...person.role_history ?? []].filter(h => +new Date(h.effective_at) <= +at).sort((a, b) => +new Date(b.effective_at) - +new Date(a.effective_at))[0];
    return row?.role ?? (person.role_history?.length ? 'rep' : person.role === 'manager' || fallback === 'manager' ? 'manager' : 'rep');
}
const easternClock = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
/** The instant an Eastern calendar day begins (day may run past the month end). Eastern midnight is 04:00 or 05:00 UTC, never inside a clock change. */
export function salesEasternDayStart(year, month, day) {
    const date = new Date(Date.UTC(year, month - 1, day)), want = date.toISOString().slice(0, 10);
    for (const hours of [4, 5]) {
        const at = new Date(+date + hours * 3600000), p = Object.fromEntries(easternClock.formatToParts(at).map(x => [x.type, x.value]));
        if (`${p.year}-${p.month}-${p.day}` === want && p.hour === '00' && p.minute === '00')
            return at;
    }
    throw new Error('Eastern day boundary is not resolvable.');
}
/** Cumulative one-twelfth split: calendar month m pays floor(A*m/12) - floor(A*(m-1)/12), so the twelve months sum to exactly A. */
export function salesMonthlyBaseCents(annualCents, month) {
    if (!Number.isSafeInteger(annualCents) || annualCents < 0 || !Number.isInteger(month) || month < 1 || month > 12)
        throw new Error('A base salary needs nonnegative cents and a calendar month.');
    const annual = BigInt(annualCents);
    return Number(annual * BigInt(month) / 12n - annual * BigInt(month - 1) / 12n);
}
/**
 * Informational payroll base lines for one Eastern month, one per (plan, role) group in force. An Eastern calendar day counts when
 * employment covers any part of it, so a start or a departure partway through a day pays that day; the payroll status, plan and role are
 * those in force at the day's last instant, so a payroll, plan or role change dated during a day governs that whole day. At that
 * instant the person must be on payroll as an employee under an accepted plan (salesPlanAt) whose rates are stage_2 with a
 * base_salary; the role comes from role_history (salesRoleAt). A group pays floor(monthly * days / days_in_month). The base is never a ledger
 * line, statement, cap input, payment reservation or Connect amount.
 */
export function salesPayrollBaseLines(person, plans, period) {
    const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period);
    if (!match)
        throw new Error('Invalid statement month.');
    const year = Number(match[1]), month = Number(match[2]), daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const groups = new Map();
    for (let day = 1; day <= daysInMonth; day++) {
        const from = salesEasternDayStart(year, month, day), at = new Date(+salesEasternDayStart(year, month, day + 1) - 1);
        if (+person.start_date > +at || (person.end_date && +person.end_date <= +from))
            continue;
        const work = salesWorkAt(person, at);
        if (work.kind !== 'employee' || work.payout_method !== 'payroll')
            continue;
        const plan = salesPlanAt(plans, at), base = plan?.rates.stage === 'stage_2' ? plan.rates.base_salary : undefined;
        if (!plan || !base)
            continue;
        const role = salesRoleAt(person, at), annual = role === 'manager' ? base.manager_annual_cents : base.rep_annual_cents, key = `${String(plan._id)}:${role}`;
        const group = groups.get(key) ?? { plan_id: String(plan._id), role, currency: base.currency, annual_base_cents: annual, monthly_cents: salesMonthlyBaseCents(annual, month), days: 0, days_in_month: daysInMonth };
        group.days++;
        groups.set(key, group);
    }
    return [...groups.values()].map(g => ({ ...g, amount_cents: Number(BigInt(g.monthly_cents) * BigInt(g.days) / BigInt(g.days_in_month)) }));
}
export function assertSalesManagerGraph(people, personId, managerId, at) {
    const person = people.find(p => String(p._id) === personId);
    if (!person)
        throw new Error('Staff record is missing.');
    const dates = [...new Set([+at, ...people.flatMap(p => (p.manager_assignment_history ?? []).map(h => +new Date(h.effective_at))).filter(t => t >= +at)])];
    for (const time of dates) {
        const seen = new Set([personId]);
        let next = managerId;
        while (next) {
            if (seen.has(next))
                throw new Error('Manager relationship would create a cycle.');
            seen.add(next);
            const manager = people.find(p => String(p._id) === next);
            if (!manager || String(manager.tenant_id) !== String(person.tenant_id) || manager.end_date && +new Date(manager.end_date) <= time || salesRoleAt(manager, new Date(time)) !== 'manager')
                throw new Error('Choose an active manager in this tenant.');
            next = salesManagerAt(manager, new Date(time));
        }
    }
}
export function salesPartnerActive(launches, approval, at) {
    const occurred = launches.filter(d => +new Date(d) <= +at);
    const recent = occurred.filter(d => +new Date(d) >= +salesAddMonths(at, -12)).length;
    if (!approval || !Number.isFinite(+new Date(approval)))
        return recent >= 8;
    if (+at < +new Date(approval))
        return false;
    return +at < +salesAddMonths(new Date(approval), 12) ? occurred.length >= 4 : recent >= 8;
}
export function salesLadderRate(rows, count) {
    return [...rows].sort((a, b) => b.min_active_partners - a.min_active_partners).find(r => count >= r.min_active_partners)?.rate_bps ?? 0;
}
/**
 * A session points ladder (partner_session_points, team_session_points) read at a count. An SP Manager book or a Sales Manager team with no
 * active partners at launch is stamped the first step, not 0 (Warren's ruling, 28 October 2026). The ledger still pays a manager line only
 * once the partner qualifies. The hardware margin ladders keep salesLadderRate, which is 0 below their first step.
 */
function salesSessionLadderCount(rows, count) {
    return Math.max(count, Math.min(...rows.map(r => r.min_active_partners)));
}
export function salesPartnerPointsRate(rates, count) {
    const points = rates.partner_session_points;
    return typeof points === 'number' ? points : salesLadderRate(points, salesSessionLadderCount(points, count));
}
/** Sales Manager session points: a stored revision 2 plan's flat number, or the ladder on the team active-partner count (ruling 4). */
export function salesTeamPointsRate(rates, teamActive) {
    const points = rates.team_session_points ?? 0;
    return typeof points === 'number' ? points : salesLadderRate(points, salesSessionLadderCount(points, teamActive));
}
/**
 * The next session points step above count, for the Today milestone (sales comp change, 27 October 2026): partner_session_points on an
 * SP Manager's active partners, team_session_points on a Sales Manager's team active partners. It reads the plan's own ladder, so the steps
 * are the ones the stamp reads, never restated. Null on a flat value (stored revisions 1 and 2) or at the top step. A count below the first
 * step already stamps the first step (28 October 2026), so its next step is the second.
 */
export function salesNextSessionStep(points, count) {
    if (points == null || typeof points === 'number')
        return null;
    const floor = salesSessionLadderCount(points, count);
    const next = [...points].sort((a, b) => a.min_active_partners - b.min_active_partners).find(r => r.min_active_partners > floor);
    return next ? { at: next.min_active_partners, needed: next.min_active_partners - count, rate_bps: next.rate_bps } : null;
}
const salesEmail = (value) => typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : null;
export function salesConsecutiveMonths(counts, target, now) {
    let held = 0;
    for (let i = 1; i <= 3; i++) {
        const date = salesAddMonths(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 15)), -i);
        if ((counts.find(r => r.period === salesMonth(date))?.count ?? 0) < target)
            break;
        held++;
    }
    return held;
}
/**
 * Partner activity at a date from loaded records: each agent's chargers stamped at or before at, counted only inside a qualifying deployment
 * whose launch was approved at or before at. The API loads these through Mongoose (salesActivityForAgents); the go-live backfill passes its
 * snapshot. Both run this one rule.
 */
export function salesActivityRows(agents, chargers, deployments, at) {
    const stamped = chargers.filter(c => agents.some(a => String(a._id) === String(c.agent_id)) && !!c.agent_commission_stamped_at && +new Date(c.agent_commission_stamped_at) <= +at);
    const approved = deployments.filter(d => d.launch_approved_by != null && !!d.launch_approved_at && +new Date(d.launch_approved_at) <= +at && !!d.created_charger_ids?.some(id => stamped.some(c => String(c._id) === String(id))));
    return agents.map(agent => {
        const launches = stamped.filter(c => String(c.agent_id) === String(agent._id) && approved.some(d => d.created_charger_ids?.some(id => String(id) === String(c._id)) && !d.owner_program && !d.fleet_id && d.origination === 'partner_originated' && String(d.location.type) !== 'residential' && d.location.type !== 'fleet' && d.access_type !== 'fleet') && !c.fleet_id).map(c => c.agent_commission_stamped_at);
        return { agent_id: String(agent._id), staff_id: String(salesAssignmentAt(agent.sales_assignment_history, at)?.sales_staff_id ?? ''), launches, active: salesPartnerActive(launches, agent.sales_approval_evidence?.approved_at, at) };
    });
}
/**
 * The launch stamp decision, from the facts at the launch instant. Null means no stamp (no rep, not qualifying, not employed, or the rep's plan
 * at launch is not revision 2). It throws where launch must stop: an already stamped charger, an unaccepted dated employment plan, a dated
 * manager without an accepted revision 2 plan, the manager role or the rep's tenant, or stamps above SALES_STAMP_MAX_BPS (900). The caller
 * writes the stamp and the payout_revision bumps on the returned rep and manager.
 * The SP Manager rate reads the rep's active partners; the Sales Manager rate reads the team active partners, counted as the team hardware
 * override counts them (salesV2Ledger teamActive: the manager's same-tenant reports at the instant, the manager's own book excluded), with
 * the launching partner's pending units counted as for the rep. Ruling 9: on a partner's charger, a rep or manager who is that partner (same
 * user, or the same normalized email on the partner's user and the staff user) is stamped 0 and listed in self_excluded.
 */
export async function salesTeamStampFor(load, charger, agentId, directStaffId, qualifies, at, pendingPartnerLaunches = []) {
    if (charger.sales_team_stamp)
        throw new Error('Sales team attribution is already stamped.');
    const agent = agentId ? await load.agent(agentId) : null;
    const assignment = agent ? salesAssignmentAt(agent.sales_assignment_history, at) : null;
    const repId = directStaffId ?? (assignment?.sales_staff_id ? String(assignment.sales_staff_id) : null);
    if (!repId || !qualifies)
        return null;
    const rep = await load.person(repId);
    if (!rep || !salesEmploymentAt(rep, at))
        return null;
    const plans = await load.acceptedPlans(rep._id), plan = salesPlanAt(plans, at);
    const work = salesWorkAt(rep, at);
    if (work.plan_id && !plans.some(p => String(p._id) === String(work.plan_id)))
        throw new Error('The dated employment plan must be accepted before launch.');
    if (plan?.rates.version !== 2)
        return null;
    const counts = (a) => agent && a.agent_id === String(agent._id) ? salesPartnerActive([...a.launches, ...pendingPartnerLaunches], agent.sales_approval_evidence?.approved_at, at) : a.active;
    const activity = await load.activity([rep._id], at);
    const active = activity.filter(a => a.staff_id === String(rep._id) && counts(a)).length;
    const own = !!directStaffId;
    const managerId = salesManagerAt(rep, at), manager = managerId ? await load.person(managerId) : null;
    const managerPlans = manager ? await load.acceptedPlans(manager._id) : [];
    const managerPlan = salesPlanAt(managerPlans, at);
    if (manager && (!salesEmploymentAt(manager, at) || salesRoleAt(manager, at) !== 'manager' || String(manager.tenant_id) !== String(rep.tenant_id) || managerPlan?.rates.version !== 2))
        throw new Error('The dated manager needs an accepted revision 2 plan before launch.');
    let teamActive = null;
    if (manager) {
        const reports = (await load.reports(manager._id, at)).filter(r => String(r.tenant_id) === String(manager.tenant_id) && String(r._id) !== String(manager._id) && salesManagerAt(r, at) === String(manager._id));
        const ids = new Set(reports.map(r => String(r._id)));
        teamActive = ids.size ? (await load.activity(reports.map(r => r._id), at)).filter(a => ids.has(a.staff_id) && counts(a)).length : 0;
    }
    const partnerUser = !own && agent?.user_id != null && String(agent.user_id) !== '' ? String(agent.user_id) : null;
    const partnerEmail = partnerUser ? salesEmail((await load.user(partnerUser))?.email) : null;
    const isPartner = async (person) => !!partnerUser && !!person && person.user_id != null
        && (String(person.user_id) === partnerUser || !!partnerEmail && salesEmail((await load.user(person.user_id))?.email) === partnerEmail);
    const self = [...await isPartner(rep) ? ['rep'] : [], ...await isPartner(manager) ? ['manager'] : []];
    const repBps = own ? charger.sales_staff_commission_bps ?? 0 : self.includes('rep') ? 0 : salesPartnerPointsRate(plan.rates, active);
    const managerBps = manager && !self.includes('manager') ? salesTeamPointsRate(managerPlan.rates, teamActive ?? 0) : 0;
    const stamp = { version: 2, at, qualifies: true, rep_id: String(rep._id), rep_plan_id: String(plan._id), rep_plan_revision: plan.revision, rep_bps: repBps, rep_category: own ? 'own_session_points' : 'partner_session_points', active_partners: active, manager_id: manager ? String(manager._id) : null, manager_plan_id: managerPlan ? String(managerPlan._id) : null, manager_plan_revision: managerPlan?.revision ?? null, manager_bps: managerBps, team_active_partners: teamActive, ...self.length ? { self_excluded: self } : {}, assignment: assignment ? { ...assignment, agent_id: String(agent._id) } : null };
    if (stamp.rep_bps + stamp.manager_bps + (charger.agent_commission_bps ?? 0) > SALES_STAMP_MAX_BPS)
        throw new Error('Sales stamps exceed the ruled session points maximum.');
    return { stamp, rep_id: rep._id, manager_id: manager ? manager._id : null };
}
//# sourceMappingURL=sales-v2.js.map